let product = null;
let selectedVariant = null;
let selectedWarranty = null;
let selectedWarrantyVariantId = null;
let inlineWidget = null;
let lastVariantId = null;
let warrantyAddInProgress = false;
let mulberryCartSyncInProgress = false;
let mulberryCartSyncQueue = Promise.resolve();
let mulberryPdpOffers = [];
let mulberryPdpReady = false;
let mulberryPdpStartAttempts = 0;
const mulberryPdpSubscribers = new Set();

const PDP_START_RETRY_MS = 500;
const PDP_START_MAX_ATTEMPTS = 120;

const BUTTONS_WRAPPER_SELECTOR =
  ".product-form__buttons";

const ADD_BUTTON_SELECTOR =
  ".product-form__submit";

const CONTAINER_ID =
  "mulberry-offer-container";

const SUBSCRIPTION_CHECKBOX_SELECTOR =
  "#add-subscription-temp";

const DEBUG_PREFIX =
  "[Mulberry PDP]";

// Expose Mulberry state to the optional theme protection-plan block (snippets\custom-mulberry-protection-plan.liquid).
function getMulberryPdpState() {
  return {
    ready: mulberryPdpReady,
    offers: mulberryPdpOffers.slice(),
    selectedWarranty: selectedWarranty,
    variantId: selectedVariant?.id || getCurrentVariantId() || null
  };
}

function notifyMulberryPdpSubscribers() {
  if (!mulberryPdpSubscribers.size) {
    return;
  }

  const state = getMulberryPdpState();

  mulberryPdpSubscribers.forEach(
    function (subscriber) {
      try {
        subscriber(state);
      } catch (error) {
        logError("Mulberry PDP subscriber failed", error);
      }
    }
  );
}

function setMulberryPdpState(
  offers = mulberryPdpOffers,
  ready = mulberryPdpReady
) {
  mulberryPdpOffers = Array.isArray(offers)
    ? offers
    : [];

  mulberryPdpReady = Boolean(ready);
  notifyMulberryPdpSubscribers();
}

function getMulberryOfferKey(offer) {
  return (
    offer?.warranty_offer_id ??
    offer?.offer_id ??
    offer?.id ??
    null
  );
}

function publishMulberryPdpBridge() {
  if (window.mulberryPdp) {
    return;
  }

  window.mulberryPdp = {
    getState: getMulberryPdpState,

    subscribe: function (subscriber) {
      if (typeof subscriber !== "function") {
        return function () {};
      }

      mulberryPdpSubscribers.add(subscriber);

      try {
        subscriber(getMulberryPdpState());
      } catch (error) {
        logError("Mulberry PDP subscriber failed", error);
      }

      return function () {
        mulberryPdpSubscribers.delete(subscriber);
      };
    },

    selectWarranty: function (
      offer,
      shouldSelect = true
    ) {
      if (!shouldSelect) {
        clearSelectedWarranty("theme protection plan deselected");
        return;
      }

      const offerId = getMulberryOfferKey(offer);
      const isCurrentOffer =
        mulberryPdpOffers.indexOf(offer) !== -1 ||
        (offerId != null && mulberryPdpOffers.some(
          function (currentOffer) {
            return (
              `${getMulberryOfferKey(currentOffer)}` ===
              `${offerId}`
            );
          }
        ));

      if (!isCurrentOffer) {
        throw new Error("Mulberry protection plan is no longer available");
      }

      storeSelectedWarranty(offer, true);
    },

    /*
     * Native Mulberry "learn more" coverage modal.
     * https://docs.getmulberry.com/reference/learn-more-modal-1
     */
    openCoverageModal: function (offer) {
      const coverage =
        offer ||
        selectedWarranty ||
        mulberryPdpOffers[0] ||
        null;

      const faq =
        window.mulberry?.faq;

      if (
        !coverage ||
        typeof faq?.init !== "function" ||
        typeof faq?.open !== "function"
      ) {
        return false;
      }

      faq.init({ coverage });
      faq.open();

      return true;
    }
  };
}

function logDebug(message, data) {
  if (typeof data === "undefined") {
    console.log(DEBUG_PREFIX, message);
    return;
  }

  console.log(
    DEBUG_PREFIX,
    message,
    data
  );
}

function logWarning(message, data) {
  if (typeof data === "undefined") {
    console.warn(DEBUG_PREFIX, message);
    return;
  }

  console.warn(
    DEBUG_PREFIX,
    message,
    data
  );
}

function logError(message, error) {
  console.error(
    DEBUG_PREFIX,
    message,
    error
  );
}

function getAddButton() {
  return document.querySelector(
    ADD_BUTTON_SELECTOR
  );
}

function getButtonsWrapper() {
  return (
    getAddButton()?.closest(
      BUTTONS_WRAPPER_SELECTOR
    ) ||
    document.querySelector(
      BUTTONS_WRAPPER_SELECTOR
    )
  );
}

function getProductForm() {
  return (
    getAddButton()?.closest("form") ||
    document.querySelector(
      'form[action*="/cart/add"]'
    )
  );
}

function normalisePositiveInteger(
  value,
  fallback = 1
) {
  const number = parseInt(value, 10);

  return Number.isFinite(number) &&
    number > 0
    ? number
    : fallback;
}

function getQuantity() {
  const form = getProductForm();

  const input =
    form?.querySelector(
      'input[name="quantity"]'
    ) ||
    document.querySelector(
      'input[name="quantity"]'
    );

  return normalisePositiveInteger(
    input?.value,
    1
  );
}

function getCurrentVariantId() {
  const form = getProductForm();

  const formVariant =
    form?.querySelector(
      '[name="id"]'
    )?.value;

  const urlVariant =
    new URLSearchParams(
      window.location.search
    ).get("variant");

  return (
    formVariant ||
    window.selectedVariantId ||
    window.mulberryShop
      ?.selectedVariantId ||
    urlVariant ||
    ""
  );
}

function ensureInlineContainer() {
  const wrapper =
    getButtonsWrapper();

  if (!wrapper?.parentNode) {
    return null;
  }

  let container =
    document.getElementById(
      CONTAINER_ID
    );

  if (!container) {
    container =
      document.createElement("div");

    container.id =
      CONTAINER_ID;

    container.className =
      "mulberry-offer-container";
  }

  if (
    container.parentNode !==
      wrapper.parentNode ||
    container.nextElementSibling !==
      wrapper
  ) {
    wrapper.parentNode.insertBefore(
      container,
      wrapper
    );
  }

  return container;
}

function findProductVariant(
  variantId
) {
  if (
    !variantId ||
    !product?.variants?.length
  ) {
    return null;
  }

  return (
    product.variants.find(
      function (variant) {
        return (
          `${variant.id}` ===
          `${variantId}`
        );
      }
    ) || null
  );
}

function getSelectedVariant(
  variantId = getCurrentVariantId()
) {
  selectedVariant =
    findProductVariant(variantId) ||
    product?.variants?.[0] ||
    null;

  if (!selectedVariant) {
    return null;
  }

  lastVariantId =
    `${selectedVariant.id}`;

  window.selectedVariant =
    selectedVariant;

  window.mulberryShop
    .selectedVariantId =
    selectedVariant.id;

  return selectedVariant;
}

async function getOffersForVariant(
  variant
) {
  if (!variant) {
    return [];
  }

  logDebug(
    "Requesting offers",
    {
      variantId:
        `${variant.id}`,

      price:
        variant.price
    }
  );

  const offers =
    await window.mulberry.core
      .getWarrantyOffer({
        detail: {
          ...product,
          id: variant.id
        },

        id:
          variant.id,

        title:
          product.title,

        price:
          variant.price
      });

  logDebug(
    "Offers received",
    {
      variantId:
        `${variant.id}`,

      offerCount:
        offers?.length || 0
    }
  );

  return offers || [];
}

function restoreSelectedWarrantyGlobals() {
  if (!selectedWarranty) {
    return;
  }

  window.selectedWarranty =
    selectedWarranty;

  window.selectedOffer =
    selectedWarranty;

  window.productForWarranty =
    product;
}

function clearSelectedWarranty(
  reason = "unspecified"
) {
  logDebug(
    "Clearing selected warranty",
    {
      reason,

      hadWarranty:
        Boolean(
          selectedWarranty
        ),

      warrantyVariantId:
        selectedWarrantyVariantId
    }
  );

  selectedWarranty = null;
  selectedWarrantyVariantId = null;

  window.selectedWarranty = null;
  window.selectedOffer = null;
  window.productForWarranty = null;

  notifyMulberryPdpSubscribers();
}

function storeSelectedWarranty(
  warranty,
  selectionState
) {
  logDebug(
    "onWarrantyToggle callback",
    {
      selectionState,

      hasWarrantyArgument:
        Boolean(warranty),

      currentVariantId:
        getCurrentVariantId()
    }
  );

  if (
    selectionState === false ||
    !warranty
  ) {
    clearSelectedWarranty(
      "Mulberry widget deselected"
    );

    return;
  }

  const offer =
    warranty?.offer ||
    warranty?.selectedOffer ||
    warranty;

  if (!offer) {
    logWarning(
      "Warranty callback did not contain an offer",
      warranty
    );

    return;
  }

  selectedWarranty =
    offer;

  selectedWarrantyVariantId =
    `${getCurrentVariantId()}`;

  restoreSelectedWarrantyGlobals();

  logDebug(
    "Warranty selected",
    {
      warrantyVariantId:
        selectedWarrantyVariantId,

      offer
    }
  );

  notifyMulberryPdpSubscribers();
}

function getCurrentWarrantySelection() {
  return (
    selectedWarranty ||
    window.selectedWarranty ||
    window.selectedOffer ||
    null
  );
}

async function refreshOffers(
  force = false
) {
  if (!product || !inlineWidget) {
    return;
  }

  const currentVariantId =
    getCurrentVariantId();

  if (
    !force &&
    currentVariantId &&
    `${currentVariantId}` ===
      `${lastVariantId}`
  ) {
    return;
  }

  clearSelectedWarranty(
    "product variant changed"
  );

  setMulberryPdpState([], false);

  if (
    inlineWidget?.deselectOffer
  ) {
    inlineWidget.deselectOffer();
  }

  const variant =
    getSelectedVariant(
      currentVariantId
    );

  const offers =
    await getOffersForVariant(
      variant
    );

  const container =
    ensureInlineContainer();

  if (container) {
    container.style.display =
      offers.length
        ? "block"
        : "none";
  }

  if (
    inlineWidget?.updateOffer
  ) {
    inlineWidget.updateOffer(
      offers
    );
  }

  setMulberryPdpState(offers, true);
}

async function addWarrantyToCart({
  offer,
  variant,
  quantity
}) {
  if (!offer || !variant) {
    logWarning(
      "Warranty add skipped because context was incomplete",
      {
        hasOffer:
          Boolean(offer),

        hasVariant:
          Boolean(variant)
      }
    );

    return false;
  }

  if (warrantyAddInProgress) {
    logWarning(
      "Warranty add skipped because another warranty add is running"
    );

    return false;
  }

  warrantyAddInProgress =
    true;

  try {
    logDebug(
      "Resolving Shopify warranty variant",
      {
        mainVariantId:
          `${variant.id}`,

        quantity,

        offer
      }
    );

    const warrantyVariant =
      await window.mulberryShop
        .getWarrantyVariant(
          offer
        );

    logDebug(
      "Warranty variant resolved",
      {
        warrantyVariant
      }
    );

    if (
      !warrantyVariant
        ?.shopify_variant_id
    ) {
      throw new Error(
        "Mulberry did not return a Shopify warranty variant."
      );
    }

    logDebug(
      "Adding warranty to cart",
      {
        warrantyShopifyVariantId:
          warrantyVariant
            .shopify_variant_id,

        connectedMainVariantId:
          `${variant.id}`,

        quantity:
          normalisePositiveInteger(
            quantity,
            1
          )
      }
    );

    await window.mulberryShop
      .addWarrantyToCart(
        warrantyVariant
          .shopify_variant_id,

        {
          ...product,

          variantId:
            `${variant.id}`,

          quantity:
            normalisePositiveInteger(
              quantity,
              1
            )
        },

        offer
      );

    logDebug(
      "Warranty successfully added to cart"
    );

    clearSelectedWarranty(
      "warranty successfully added"
    );

    return true;
  } finally {
    warrantyAddInProgress =
      false;
  }
}

function isCartAddRequest(input) {
  const url =
    typeof input === "string"
      ? input
      : input?.url || "";

  try {
    const parsedUrl =
      new URL(
        url,
        window.location.origin
      );

    return (
      parsedUrl.pathname ===
        "/cart/add" ||
      parsedUrl.pathname ===
        "/cart/add.js"
    );
  } catch (error) {
    return url.includes(
      "/cart/add"
    );
  }
}

function getMatchingProductLine(
  items
) {
  if (!Array.isArray(items)) {
    return null;
  }

  return (
    items.find(
      function (item) {
        return Boolean(
          findProductVariant(
            item?.id ||
              item?.variant_id
          )
        );
      }
    ) || null
  );
}

function parseKeyValueCartBody(
  collection
) {
  const directVariantId =
    collection.get("id");

  const directQuantity =
    collection.get("quantity");

  if (directVariantId) {
    return {
      variantId:
        `${directVariantId}`,

      quantity:
        directQuantity,

      itemIds: [
        `${directVariantId}`
      ],

      source:
        "direct fields"
    };
  }

  const itemIds = [];
  const quantitiesByIndex = {};

  for (
    const [key, value]
    of collection.entries()
  ) {
    const idMatch =
      key.match(
        /^items\[(\d+)\]\[(?:id|variant_id)\]$/
      );

    if (idMatch) {
      itemIds.push(
        `${value}`
      );
    }

    const quantityMatch =
      key.match(
        /^items\[(\d+)\]\[quantity\]$/
      );

    if (quantityMatch) {
      quantitiesByIndex[
        quantityMatch[1]
      ] = value;
    }
  }

  let matchingVariantId = null;
  let matchingQuantity = null;

  for (
    const [key, value]
    of collection.entries()
  ) {
    const idMatch =
      key.match(
        /^items\[(\d+)\]\[(?:id|variant_id)\]$/
      );

    if (
      idMatch &&
      findProductVariant(value)
    ) {
      matchingVariantId =
        `${value}`;

      matchingQuantity =
        quantitiesByIndex[
          idMatch[1]
        ] || null;

      break;
    }
  }

  return {
    variantId:
      matchingVariantId,

    quantity:
      matchingQuantity,

    itemIds,

    source:
      "items fields"
  };
}

function parseCartBodyValue(body) {
  if (!body) {
    return {
      variantId: null,
      quantity: null,
      itemIds: [],
      source: "empty body"
    };
  }

  if (
    body instanceof FormData
  ) {
    return parseKeyValueCartBody(
      body
    );
  }

  if (
    body instanceof
      URLSearchParams
  ) {
    return parseKeyValueCartBody(
      body
    );
  }

  if (
    typeof body === "object"
  ) {
    if (
      Array.isArray(body.items)
    ) {
      const matchingLine =
        getMatchingProductLine(
          body.items
        );

      return {
        variantId:
          matchingLine?.id ||
          matchingLine
            ?.variant_id ||
          null,

        quantity:
          matchingLine
            ?.quantity ||
          null,

        itemIds:
          body.items
            .map(
              function (item) {
                return `${(
                  item?.id ||
                  item?.variant_id ||
                  ""
                )}`;
              }
            )
            .filter(Boolean),

        source:
          "JSON items array"
      };
    }

    return {
      variantId:
        body.id ||
        body.variant_id ||
        null,

      quantity:
        body.quantity ||
        null,

      itemIds: [
        `${(
          body.id ||
          body.variant_id ||
          ""
        )}`
      ].filter(Boolean),

      source:
        "JSON object"
    };
  }

  if (
    typeof body !== "string"
  ) {
    return {
      variantId: null,
      quantity: null,
      itemIds: [],
      source: typeof body
    };
  }

  const trimmedBody =
    body.trim();

  if (!trimmedBody) {
    return {
      variantId: null,
      quantity: null,
      itemIds: [],
      source: "empty string"
    };
  }

  try {
    return parseCartBodyValue(
      JSON.parse(
        trimmedBody
      )
    );
  } catch (error) {
    return parseKeyValueCartBody(
      new URLSearchParams(
        trimmedBody
      )
    );
  }
}

async function getCartAddContext(
  input,
  options
) {
  let body =
    options?.body || null;

  if (
    !body &&
    typeof Request !==
      "undefined" &&
    input instanceof Request
  ) {
    try {
      body =
        await input
          .clone()
          .text();
    } catch (error) {
      logWarning(
        "Could not read Request body",
        error
      );
    }
  }

  const parsedBody =
    parseCartBodyValue(body);

  const requestVariant =
    findProductVariant(
      parsedBody.variantId
    );

  return {
    parsedBody,

    variant:
      requestVariant,

    quantity:
      normalisePositiveInteger(
        parsedBody.quantity,
        getQuantity()
      ),

    isMainProductRequest:
      Boolean(requestVariant)
  };
}

function installCartAddInterceptor() {
  if (
    window
      .__mulberryCartAddInterceptorInstalled
  ) {
    return;
  }

  window
    .__mulberryCartAddInterceptorInstalled =
    true;

  const originalFetch =
    window.fetch.bind(window);

  window.fetch =
    async function (
      input,
      options
    ) {
      if (
        !isCartAddRequest(input) ||
        warrantyAddInProgress
      ) {
        return originalFetch(
          input,
          options
        );
      }

      const offer =
        getCurrentWarrantySelection();

      const cartContext =
        await getCartAddContext(
          input,
          options
        );

      logDebug(
        "Intercepted /cart/add request",
        {
          parsedBody:
            cartContext.parsedBody,

          isMainProductRequest:
            cartContext
              .isMainProductRequest,

          matchedVariantId:
            cartContext.variant
              ? `${(
                  cartContext
                    .variant.id
                )}`
              : null,

          currentProductVariantId:
            getCurrentVariantId(),

          hasSelectedWarranty:
            Boolean(offer),

          selectedWarrantyVariantId
        }
      );

      /*
       * Checkbox/byproduct request.
       *
       * Its variant does not belong to this PDP product,
       * so let it run normally and keep the selected offer.
       */
      if (
        !cartContext
          .isMainProductRequest
      ) {
        logDebug(
          "Passing through non-main/byproduct cart add"
        );

        const response =
          await originalFetch(
            input,
            options
          );

        restoreSelectedWarrantyGlobals();

        logDebug(
          "Byproduct cart add completed",
          {
            status:
              response.status,

            hasSelectedWarrantyAfterRequest:
              Boolean(
                getCurrentWarrantySelection()
              )
          }
        );

        return response;
      }

      /*
       * The request contains a variant belonging to this
       * PDP product, so this is the actual main product.
       */
      if (!offer) {
        logWarning(
          "Main product request detected, but no selected Mulberry offer was found"
        );

        return originalFetch(
          input,
          options
        );
      }

      if (
        selectedWarrantyVariantId &&
        `${selectedWarrantyVariantId}` !==
          `${cartContext.variant.id}`
      ) {
        logWarning(
          "Selected warranty belongs to another variant",
          {
            selectedWarrantyVariantId,

            requestVariantId:
              `${(
                cartContext
                  .variant.id
              )}`
          }
        );

        return originalFetch(
          input,
          options
        );
      }

      const warrantyContext = {
        offer,

        variant:
          cartContext.variant,

        quantity:
          cartContext.quantity
      };

      logDebug(
        "Main product request matched; sending main product first",
        {
          variantId:
            `${(
              warrantyContext
                .variant.id
            )}`,

          quantity:
            warrantyContext
              .quantity
        }
      );

      const response =
        await originalFetch(
          input,
          options
        );

      logDebug(
        "Main product response received",
        {
          status:
            response.status,

          ok:
            response.ok
        }
      );

      if (!response.ok) {
        logWarning(
          "Main product failed, so warranty was not added"
        );

        return response;
      }

      try {
        await addWarrantyToCart(
          warrantyContext
        );
      } catch (error) {
        logError(
          "Main product was added, but warranty add failed",
          error
        );
      }

      return response;
    };

  logDebug(
    "Cart add interceptor installed"
  );
}

function bindSubscriptionCheckboxDebug() {
  if (
    window
      .__mulberrySubscriptionCheckboxDebugBound
  ) {
    return;
  }

  window
    .__mulberrySubscriptionCheckboxDebugBound =
    true;

  document.addEventListener(
    "change",
    function (event) {
      const target =
        event.target;

      if (
        !(target instanceof Element) ||
        !target.matches(
          SUBSCRIPTION_CHECKBOX_SELECTOR
        )
      ) {
        return;
      }

      logDebug(
        "Subscription checkbox changed",
        {
          checked:
            target.checked,

          hasSelectedWarranty:
            Boolean(
              getCurrentWarrantySelection()
            ),

          selectedWarrantyVariantId
        }
      );
    },
    true
  );
}

function isCartChangeOrUpdateRequest(
  input
) {
  const url =
    typeof input === "string"
      ? input
      : input?.url || "";

  try {
    const parsedUrl =
      new URL(
        url,
        window.location.origin
      );

    return (
      parsedUrl.pathname ===
        "/cart/change" ||
      parsedUrl.pathname ===
        "/cart/change.js" ||
      parsedUrl.pathname ===
        "/cart/update" ||
      parsedUrl.pathname ===
        "/cart/update.js"
    );
  } catch (error) {
    return (
      url.includes(
        "/cart/change"
      ) ||
      url.includes(
        "/cart/update"
      )
    );
  }
}

function getConnectedMainVariantId(
  cartItem
) {
  return (
    cartItem
      ?.properties
      ?._variantId ||
    cartItem
      ?.properties
      ?._variant_id ||
    ""
  );
}

function isMulberryWarrantyItem(
  cartItem
) {
  /*
   * Mulberry adds the connected main-product variant
   * inside properties._variantId.
   */
  return Boolean(
    getConnectedMainVariantId(
      cartItem
    )
  );
}

async function getCurrentCart(
  fetchFunction
) {
  const cartUrl =
    `${(
      window.Shopify
        ?.routes
        ?.root ||
      "/"
    )}cart.js`;

  const response =
    await fetchFunction(
      cartUrl,
      {
        method: "GET",

        credentials:
          "same-origin",

        headers: {
          Accept:
            "application/json"
        }
      }
    );

  if (!response.ok) {
    throw new Error(
      `Unable to fetch cart (${response.status}).`
    );
  }

  return response.json();
}

async function getCartJson() {
  const cartUrl =
    `${(
      window.Shopify
        ?.routes
        ?.root ||
      "/"
    )}cart.js`;

  const response =
    await fetch(
      cartUrl,
      {
        method: "GET",

        credentials:
          "same-origin",

        headers: {
          Accept:
            "application/json"
        }
      }
    );

  if (!response.ok) {
    throw new Error(
      `Unable to fetch cart (${response.status}).`
    );
  }

  return response.json();
}

function isSectionalMulberryItem(
  item
) {
  const properties =
    item?.properties || {};

  return Boolean(
    properties._warranty_duration_months ||
    properties._variantId
  );
}

async function removeOrphanMulberryWarrantyItems() {
  const cart =
    await getCartJson();

  const normalVariantIds =
    new Set(
      cart.items
        .filter(
          function (item) {
            return !isMulberryWarrantyItem(
              item
            );
          }
        )
        .map(
          function (item) {
            return `${(
              item.variant_id ||
              item.id
            )}`;
          }
        )
    );

  const orphanWarrantyItems =
    cart.items.filter(
      function (item) {
        if (
          !isMulberryWarrantyItem(
            item
          )
        ) {
          return false;
        }

        const connectedVariantId =
          `${(
            getConnectedMainVariantId(
              item
            )
          )}`;

        return (
          !connectedVariantId ||
          !normalVariantIds.has(
            connectedVariantId
          )
        );
      }
    );

  for (
    const item
    of orphanWarrantyItems
  ) {
    if (!item.key) {
      continue;
    }

    logDebug(
      "Removing orphan Mulberry warranty",
      {
        warrantyLineKey:
          item.key,

        connectedVariantId:
          getConnectedMainVariantId(
            item
          )
      }
    );

    const response =
      await fetch(
        `${(
          window.Shopify
            ?.routes
            ?.root ||
          "/"
        )}cart/change.js`,
        {
          method: "POST",

          credentials:
            "same-origin",

          headers: {
            "Content-Type":
              "application/json",

            Accept:
              "application/json"
          },

          body:
            JSON.stringify({
              id:
                item.key,

              quantity:
                0
            })
        }
      );

    if (!response.ok) {
      throw new Error(
        `Unable to remove orphan warranty (${response.status}).`
      );
    }
  }
}

function buildMulberryQuantityUpdates(
  cart
) {
  const items =
    Array.isArray(
      cart?.items
    )
      ? cart.items
      : [];

  /*
   * Store the total quantity of every normal cart variant.
   *
   * Key:
   * main Shopify variant ID
   *
   * Value:
   * combined quantity of that variant in the cart
   */
  const mainQuantities =
    new Map();

  items.forEach(
    function (item) {
      if (
        isMulberryWarrantyItem(
          item
        )
      ) {
        return;
      }

      const variantId =
        item?.variant_id ||
        item?.id;

      if (!variantId) {
        return;
      }

      const key =
        `${variantId}`;

      const currentQuantity =
        parseInt(
          item.quantity,
          10
        ) || 0;

      const existingQuantity =
        mainQuantities.get(
          key
        ) || 0;

      mainQuantities.set(
        key,
        existingQuantity +
          currentQuantity
      );
    }
  );

  const updates = {};
  const debugItems = [];

  items.forEach(
    function (item) {
      if (
        !isMulberryWarrantyItem(
          item
        )
      ) {
        return;
      }

      const connectedVariantId =
        `${(
          getConnectedMainVariantId(
            item
          )
        )}`;

      /*
       * When the main product no longer exists,
       * this resolves to zero and removes the warranty.
       */
      const desiredQuantity =
        mainQuantities.get(
          connectedVariantId
        ) || 0;

      const currentQuantity =
        parseInt(
          item.quantity,
          10
        ) || 0;

      debugItems.push({
        warrantyLineKey:
          item.key,

        connectedVariantId,

        currentQuantity,

        desiredQuantity
      });

      if (
        item.key &&
        currentQuantity !==
          desiredQuantity
      ) {
        /*
         * Shopify permits line-item keys inside the
         * updates object.
         */
        updates[item.key] =
          desiredQuantity;
      }
    }
  );

  return {
    updates,
    debugItems
  };
}

async function rerenderMulberryCart() {
  logDebug(
    "Re-rendering Mulberry cart"
  );

  try {
    if (
      window.mulberryShop
        ?.cart
        ?.render
    ) {
      await window
        .mulberryShop
        .cart
        .render({
          refresh: true
        });
    }
  } catch (error) {
    logWarning(
      "Mulberry cart render failed",
      error
    );
  }
}

async function removeAndSyncMulberryOffers() {
  if (
    mulberryCartSyncInProgress
  ) {
    logDebug(
      "Mulberry cart sync already in progress"
    );

    return false;
  }

  mulberryCartSyncInProgress =
    true;

  try {
    const firstCart =
      await getCartJson();

    logDebug(
      "Starting Mulberry warranty removal and quantity sync",
      {
        items:
          firstCart.items.map(
            function (item) {
              return {
                key:
                  item.key,

                variantId:
                  item.variant_id,

                quantity:
                  item.quantity,

                properties:
                  item.properties,

                connectedVariantId:
                  getConnectedMainVariantId(
                    item
                  )
              };
            }
          )
      }
    );

    try {
      const newCart =
        await window
          .mulberryShop
          .removeDetachedWarranty(
            firstCart
          );

      logDebug(
        "removeDetachedWarranty result",
        newCart
      );

      await window
        .mulberryShop
        .cart
        .syncQuantities(
          newCart || firstCart
        );

      logDebug(
        "Mulberry warranty quantities synced"
      );
    } catch (error) {
      logError(
        "Error during warranty removal and cart sync",
        error
      );
    }

    try {
      await removeOrphanMulberryWarrantyItems();

      logDebug(
        "Orphan Mulberry warranty cleanup completed"
      );
    } catch (error) {
      logError(
        "Error during orphan Mulberry warranty cleanup",
        error
      );
    }

    await rerenderMulberryCart();

    return true;
  } catch (error) {
    logError(
      "Unable to remove and sync Mulberry warranties",
      error
    );

    return false;
  } finally {
    mulberryCartSyncInProgress =
      false;
  }
}

function queueMulberryCartSync() {
  const operation =
    mulberryCartSyncQueue.then(
      function () {
        return removeAndSyncMulberryOffers();
      },

      function () {
        return removeAndSyncMulberryOffers();
      }
    );

  mulberryCartSyncQueue =
    operation.catch(
      function () {}
    );

  return operation;
}

function bindVariantChanges() {
  if (
    window
      .__mulberryVariantListenersBound
  ) {
    return;
  }

  window
    .__mulberryVariantListenersBound =
    true;

  let refreshTimer = null;

  function scheduleRefresh() {
    window.clearTimeout(
      refreshTimer
    );

    refreshTimer =
      window.setTimeout(
        function () {
          refreshOffers().catch(
            function (error) {
              logError(
                "Unable to refresh offers",
                error
              );
            }
          );
        },
        180
      );
  }

  document.addEventListener(
    "change",
    function (event) {
      const target =
        event.target;

      if (
        !(target instanceof Element)
      ) {
        return;
      }

      if (
        target.matches(
          '[name="id"]'
        ) ||
        target.closest(
          "variant-selects"
        ) ||
        target.closest(
          "variant-radios"
        )
      ) {
        scheduleRefresh();
      }
    }
  );

  document.addEventListener(
    "click",
    function (event) {
      const target =
        event.target;

      if (
        !(target instanceof Element)
      ) {
        return;
      }

      if (
        target.closest(
          "[data-option-value]"
        ) ||
        target.closest(
          "[data-variant-id]"
        ) ||
        target.closest(
          "variant-selects"
        ) ||
        target.closest(
          "variant-radios"
        )
      ) {
        scheduleRefresh();
      }
    }
  );

  window.addEventListener(
    "popstate",
    scheduleRefresh
  );
}

async function initMulberryPdp() {
  if (
    window.__mulberryPdpInitialized
  ) {
    return;
  }

  const container =
    ensureInlineContainer();

  if (!container) {
    logWarning(
      "Could not find product buttons wrapper"
    );

    return;
  }

  window.__mulberryPdpInitialized =
    true;

  window.mb_gtm_is_disabled =
    true;

  window.mulberry.core.settings
    .has_inline = true;

  window.mulberry.core.settings
    .has_modal = false;

  installCartAddInterceptor();
  bindSubscriptionCheckboxDebug();
  bindVariantChanges();

  logDebug(
    "Loading product data"
  );

  const productData =
    await window.mulberryShop
      .getProductData(
        window.mulberryShop
          .getProductUrl()
      );

  product =
    productData.product;

  logDebug(
    "Product data loaded",
    {
      productId:
        product?.id,

      title:
        product?.title,

      variantIds:
        product?.variants?.map(
          function (variant) {
            return `${variant.id}`;
          }
        ) || []
    }
  );

  const variant =
    getSelectedVariant();

  const offers =
    await getOffersForVariant(
      variant
    );

  container.style.display =
    offers.length
      ? "block"
      : "none";

  inlineWidget =
    await window.mulberry.inline.init({
      selector:
        `#${CONTAINER_ID}`,

      offers,

      settings:
        window.mulberry.core
          .settings,

      onWarrantyToggle:
        function (
          warranty,
          isSelected
        ) {
          storeSelectedWarranty(
            warranty,
            isSelected
          );
        }
    });

  ensureInlineContainer();

  setMulberryPdpState(offers, true);

  window.__mulberryDebugState =
  function () {
    return {
      currentVariantId:
        getCurrentVariantId(),

      selectedWarranty,

      selectedWarrantyVariantId,

      warrantyAddInProgress,

      mulberryCartSyncInProgress,

      productVariantIds:
        product?.variants?.map(
          function (variant) {
            return `${variant.id}`;
          }
        ) || []
    };
  };

  logDebug(
    "Mulberry PDP initialised. Run window.__mulberryDebugState() to inspect the current state."
  );
}

function startMulberryPdp() {
  if (
    !window.location.pathname
      .includes("/products/")
  ) {
    return;
  }

  /*
   * The SDK loads asynchronously and can land after both DOMContentLoaded and mulberry-shopify:loaded have fired, and nothing else retries.
   */
  if (
    !window.mulberryShop ||
    !window.mulberry?.core?.settings ||
    !window.mulberry?.inline
  ) {
    if (
      mulberryPdpStartAttempts <
      PDP_START_MAX_ATTEMPTS
    ) {
      mulberryPdpStartAttempts += 1;

      window.setTimeout(
        startMulberryPdp,
        PDP_START_RETRY_MS
      );
    }

    return;
  }

  initMulberryPdp().catch(
    function (error) {
      window.__mulberryPdpInitialized =
        false;

      setMulberryPdpState([], false);

      logError(
        "Unable to initialise Mulberry PDP",
        error
      );
    }
  );
}

publishMulberryPdpBridge();

document.addEventListener(
  "mulberry-shopify:loaded",
  function () {
    startMulberryCartSync();
    startMulberryPdp();
  }
);

if (
  document.readyState ===
  "loading"
) {
  document.addEventListener(
    "DOMContentLoaded",
    function () {
      startMulberryCartSync();
      startMulberryPdp();
    }
  );
} else {
  startMulberryCartSync();
  startMulberryPdp();
}

function installCartChangeSyncInterceptor() {
  if (
    window
      .__mulberryCartChangeSyncInstalled
  ) {
    return;
  }

  window
    .__mulberryCartChangeSyncInstalled =
    true;

  const previousFetch =
    window.fetch.bind(window);

  window.fetch =
    async function (
      input,
      options
    ) {
      /*
       * Ignore normal requests and any cart requests
       * generated internally while Mulberry is syncing.
       */
      if (
        mulberryCartSyncInProgress ||
        !isCartChangeOrUpdateRequest(
          input
        )
      ) {
        return previousFetch(
          input,
          options
        );
      }

      const requestUrl =
        typeof input === "string"
          ? input
          : input?.url || "";

      logDebug(
        "Detected theme cart line-item request",
        {
          url:
            requestUrl,

          body:
            options?.body || null
        }
      );

      /*
       * First allow UpCart's requested cart change to
       * complete normally.
       */
      const originalResponse =
        await previousFetch(
          input,
          options
        );

      logDebug(
        "Theme cart line-item request completed",
        {
          status:
            originalResponse.status,

          ok:
            originalResponse.ok
        }
      );

      /*
       * Preserve unsuccessful responses exactly as they
       * were returned by Shopify.
       */
      if (!originalResponse.ok) {
        return originalResponse;
      }

      try {
        /*
         * Important:
         *
         * Do not return the original response to UpCart yet.
         * UpCart would render that response before Mulberry
         * finishes synchronising the warranty quantity.
         */
        const synced =
          await queueMulberryCartSync();

        logDebug(
          "Mulberry cart quantity sync completed before UpCart redraw",
          {
            synced
          }
        );

        /*
         * Fetch the final cart state after Mulberry has
         * updated the warranty quantity.
         */
        const finalCart =
          await getCartJson();

        logDebug(
          "Returning final synced cart to UpCart",
          {
            itemCount:
              finalCart.item_count,

            items:
              finalCart.items.map(
                function (item) {
                  return {
                    key:
                      item.key,

                    quantity:
                      item.quantity,

                    connectedVariantId:
                      getConnectedMainVariantId(
                        item
                      )
                  };
                }
              )
          }
        );

        /*
         * UpCart is awaiting response.json().
         *
         * Return a new Response containing the final cart,
         * so UpCart renders the synced Mulberry quantity
         * through its own normal rendering mechanism.
         */
        return new Response(
          JSON.stringify(
            finalCart
          ),
          {
            status:
              originalResponse.status,

            statusText:
              originalResponse.statusText,

            headers: {
              "Content-Type":
                "application/json",

              "Cache-Control":
                "no-store"
            }
          }
        );
      } catch (error) {
        logError(
          "Mulberry sync failed before UpCart response",
          error
        );

        /*
         * Fall back to the original successful response
         * so the cart interaction still completes.
         */
        return originalResponse;
      }
    };

  logDebug(
    "Cart change sync interceptor installed"
  );
}

function startMulberryCartSync() {
  if (
    !window.mulberryShop
  ) {
    return;
  }

  installCartChangeSyncInterceptor();

  logDebug(
    "Global Mulberry cart sync started"
  );
}
